import { memo, useMemo } from 'react'
import { Layer, Source } from '@vis.gl/react-maplibre'
import {
  getCommunityMapLayerSourceId,
  hasValidCommunityMapLayerAttribute,
} from '../../utils/community-map-layer-utils'

const PALETTE_ITEMS = [
  { name: 'red', colors: ['#DDB100', '#DD8500', '#DD5800', '#DD2C00', '#DD0000'] },
  { name: 'blue', colors: ['#00A0CB', '#0079B8', '#0051A6', '#002A93', '#000280'] },
  { name: 'gray', colors: ['#BBBEBF', '#959798', '#6F7171', '#494A4A', '#232323'] },
]

const DEFAULT_PALETTE = PALETTE_ITEMS.find((item) => item.name === 'gray')

function resolvePalette(paletteName) {
  return PALETTE_ITEMS.find((item) => item.name === paletteName) ?? DEFAULT_PALETTE
}

function buildBasicLayerConfigs(layerId, sourceId, palette) {
  return {
    fill: {
      id: `layer-fill-${layerId}`,
      type: 'fill',
      source: sourceId,
      filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
      paint: {
        'fill-color': palette.colors[2],
        'fill-opacity': 0.35,
        'fill-outline-color': palette.colors[4],
      },
    },
    line: {
      id: `layer-line-${layerId}`,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': palette.colors[2], 'line-width': 3 },
    },
    point: {
      id: `layer-point-${layerId}`,
      type: 'circle',
      source: sourceId,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-color': palette.colors[4], 'circle-radius': 6 },
    },
  }
}

function buildAttributeLayerConfigs(layerId, sourceId, palette, mapAttribute, maxValue) {
  const minWidth = 7
  const widthStep = 1
  return {
    fill: {
      id: `layer-fill-${layerId}`,
      type: 'fill',
      source: sourceId,
      filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
      paint: {
        'fill-color': [
          'interpolate', ['linear'], ['to-number', ['get', mapAttribute]],
          0, palette.colors[0],
          maxValue * 0.25, palette.colors[1],
          maxValue * 0.5, palette.colors[2],
          maxValue * 0.75, palette.colors[3],
          maxValue, palette.colors[4],
        ],
        'fill-opacity': 0.35,
        'fill-outline-color': palette.colors[4],
      },
    },
    line: {
      id: `layer-${layerId}`,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': [
          'interpolate', ['linear'], ['to-number', ['get', mapAttribute]],
          0, palette.colors[0],
          maxValue * 0.25, palette.colors[1],
          maxValue * 0.5, palette.colors[2],
          maxValue * 0.75, palette.colors[3],
          maxValue, palette.colors[4],
        ],
        'line-width': [
          'interpolate', ['linear'], ['to-number', ['get', mapAttribute]],
          0, minWidth,
          maxValue * 0.25, minWidth + widthStep,
          maxValue * 0.5, minWidth + widthStep * 2,
          maxValue * 0.75, minWidth + widthStep * 3,
          maxValue, minWidth + widthStep * 4,
        ],
      },
    },
  }
}

function resolveMaxAttributeValue(features, mapAttribute) {
  if (!features?.length) return 0
  const values = features
    .map((feature) => Number.parseFloat(String(feature.properties?.[mapAttribute] ?? '')))
    .filter((value) => Number.isFinite(value))
  if (values.length === 0) return 0
  return Math.max(...values)
}

export const CommunityMapLayer = memo(function CommunityMapLayer({ layer }) {
  const palette = resolvePalette(layer.palette)
  const mapAttribute = layer.mapAttribute || ''
  const hasAttributeConfig = Boolean(mapAttribute && layer.palette)
  const sourceId = getCommunityMapLayerSourceId(layer.id)
  const geoJson = layer.fileContent

  const hasAttribute = useMemo(() => {
    if (!hasAttributeConfig) return false
    return hasValidCommunityMapLayerAttribute(geoJson?.features, mapAttribute, layer.palette)
  }, [geoJson?.features, hasAttributeConfig, layer.palette, mapAttribute])

  const maxAttributeValue = useMemo(
    () => (hasAttribute ? resolveMaxAttributeValue(geoJson?.features, mapAttribute) : 0),
    [geoJson?.features, hasAttribute, mapAttribute],
  )

  const layerConfigs = useMemo(() => {
    if (hasAttribute) {
      return buildAttributeLayerConfigs(layer.id, sourceId, palette, mapAttribute, maxAttributeValue)
    }
    return buildBasicLayerConfigs(layer.id, sourceId, palette)
  }, [hasAttribute, layer.id, mapAttribute, maxAttributeValue, palette, sourceId])

  if (!geoJson?.features?.length) return null

  return (
    <Source id={sourceId} type="geojson" data={geoJson} lineMetrics>
      {hasAttribute ? (
        <>
          <Layer {...layerConfigs.fill} />
          <Layer {...layerConfigs.line} />
        </>
      ) : (
        <>
          <Layer {...layerConfigs.fill} />
          <Layer {...layerConfigs.line} />
          <Layer {...layerConfigs.point} />
        </>
      )}
    </Source>
  )
})
